import React, { useMemo } from 'react';
import { X, Trash2, ExternalLink, Layers, FileText, Store, Plus, Minus, ShoppingCart } from 'lucide-react';
import { STORE_MAP } from '../data/stores';
import './UniversalCart.css';

export default function UniversalCart({
  isOpen,
  onClose,
  items,
  onRemoveItem,
  onUpdateItemQuantity,
  onClearAll,
}) {
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const groupedByStore = useMemo(() => {
    return items.reduce((acc, item) => {
      const fallback = item.component?.stores?.[0]?.storeId || 'unassigned';
      const key = item.selectedStoreId || fallback;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
  }, [items]);

  const storeKeys = Object.keys(groupedByStore);

  const openAllForStore = (storeId) => {
    const storeMeta = STORE_MAP[storeId];
    if (!storeMeta) return;
    groupedByStore[storeId].forEach((item) => {
      const selectedStoreEntry = (item.component.stores || []).find((s) => s.storeId === storeId);
      const query = selectedStoreEntry?.searchQuery || item.component.name;
      window.open(storeMeta.searchUrl + encodeURIComponent(query), '_blank');
    });
  };

  const checkoutAll = () => {
    storeKeys.forEach((storeId) => {
      openAllForStore(storeId);
    });
  };

  return (
    <>
      {isOpen && <button className="cart-overlay" onClick={onClose} aria-label="Close universal cart overlay" />}
      <aside className={`universal-cart ${isOpen ? 'open' : ''}`}>
        <div className="uc-head">
          <div className="uc-title-wrap">
            <div className="uc-title">Universal Cart</div>
            <div className="uc-sub mono">
              <Layers size={12} /> {items.length} items across {storeKeys.length || 0} stores
            </div>
          </div>
          <button
            className="uc-close"
            onPointerDown={handleClose}
            onClick={handleClose}
            type="button"
            aria-label="Close universal cart"
          >
            <X size={16} />
          </button>
        </div>

        <div className="uc-body">
          {items.length === 0 && (
            <div className="uc-empty">
              <div className="uc-empty-title">No items yet</div>
              <div className="uc-empty-text">Add components from any project and they will stay here.</div>
            </div>
          )}

          {storeKeys.map((storeId) => {
            const store = STORE_MAP[storeId] || {
              name: 'Unassigned Store',
              logo: '',
              color: '#7b7f96',
              searchUrl: '',
              tag: 'Choose a store in component card',
            };

            return (
              <section key={storeId} className="uc-group">
                <div className="uc-group-title" style={{ borderLeftColor: store.color }}>
                  <div className="uc-group-title-main">
                    <Store size={12} /> {store.logo} {store.name}
                  </div>
                  <div className="uc-group-actions">
                    {store.searchUrl && (
                      <button className="uc-open-all" onClick={() => openAllForStore(storeId)}>
                        Open all <ExternalLink size={11} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="uc-list">
                  {groupedByStore[storeId].map((item) => {
                    const selectedStoreEntry = (item.component.stores || []).find((s) => s.storeId === storeId);
                    const buyUrl = store.searchUrl
                      ? store.searchUrl + encodeURIComponent(selectedStoreEntry?.searchQuery || item.component.name)
                      : null;

                    return (
                      <article key={item.key} className="uc-item">
                        <div className="uc-item-main">
                          <div className="uc-item-name">{item.component.name}</div>
                          <div className="uc-item-meta mono">
                            {item.projectTitle} . Qty: {item.quantity || 1} . {selectedStoreEntry?.estimatedPrice || item.component.estimatedPrice}
                          </div>
                        </div>
                        <div className="uc-item-actions">
                          <div className="uc-qty" aria-label="Item quantity controls">
                            <button
                              className="uc-qty-btn"
                              onClick={() => onUpdateItemQuantity(item.key, (item.quantity || 1) - 1)}
                              aria-label="Decrease quantity"
                            >
                              <Minus size={10} />
                            </button>
                            <span className="uc-qty-value mono">{item.quantity || 1}</span>
                            <button
                              className="uc-qty-btn"
                              onClick={() => onUpdateItemQuantity(item.key, (item.quantity || 1) + 1)}
                              aria-label="Increase quantity"
                            >
                              <Plus size={10} />
                            </button>
                          </div>
                          {item.component.datasheetUrl && (
                            <a className="uc-data" href={item.component.datasheetUrl} target="_blank" rel="noreferrer">
                              <FileText size={11} />
                            </a>
                          )}
                          {buyUrl && (
                            <a className="uc-buy" href={buyUrl} target="_blank" rel="noreferrer">
                              Buy <ExternalLink size={11} />
                            </a>
                          )}
                          <button className="uc-remove" onClick={() => onRemoveItem(item.key)} aria-label="Remove cart item">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <div className="uc-foot">
          <button className="uc-checkout" onClick={checkoutAll} disabled={items.length === 0}>
            <ShoppingCart size={14} /> Checkout All
          </button>
          <button className="uc-clear" onClick={onClearAll} disabled={items.length === 0}>
            <Trash2 size={14} /> Clear Universal Cart
          </button>
        </div>
      </aside>
    </>
  );
}
